import { createSlice } from "@reduxjs/toolkit";
import { organisationApi } from "../../api/organisationApi";
import { toast } from "../../components/common/toast";
import type { AppDispatch, RootState } from "../../app/store";
import type { Organisation } from "../../types/organisationSettings.types";
import { getCurrentUser } from "../../auth/auth.service";
import i18n from "../../i18n";
import {
  applyDelete,
  applyRename,
  buildDeletePayload,
  buildRenamePayload,
  createResolvedMedia,
  uploadMediaFiles,
  applyCover,
  buildCoverPayload,
} from "../../utils/mediaUtils";
import { getApiErrorMessage } from "../../utils/apiError";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";

// The newest first-page request (see fetchOrganisations).
let latestOrganisationsPage = 0;

export const fetchOrganisations = (payload: any = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().organisations;
    const userData = await getCurrentUser();

    const incomingPageIndex = payload.hasOwnProperty("pageIndex")
      ? payload.pageIndex
      : state.pageIndex;

    // A first page always runs (a new search must not be dropped because an
    // earlier one is still loading); an answer to an older one is ignored.
    const isFirstPage = incomingPageIndex === 0;
    if ((state.isLoading || !state.hasMore) && !isFirstPage) return;
    const ticket = isFirstPage
      ? ++latestOrganisationsPage
      : latestOrganisationsPage;
    const superseded = () => ticket !== latestOrganisationsPage;

    try {
      dispatch(setOrganisationLoading(true));

      const requestBody = {
        pageSize: 10,
        pageIndex: incomingPageIndex,
        // Most recently updated organisation first.
        orderBy: payload.orderBy || "modificationdate",
        // "Sort by" from the listing (resolveSort gives sortAscending; this
        // endpoint takes the opposite flag).
        sortDecending: !(payload.sortAscending ?? false),
        groupIds: [`${userData.companyId}`],
        ids: payload.ids || [],
        isActive: payload.isActive ?? null,
        code: payload.code || "",
        name: payload.name || "",
        primaryEmail: payload.primaryEmail || "",
        primaryPhone: payload.primaryPhone || "",
        searchText: payload.searchText || "",
        resolvePic: payload.hasOwnProperty("resolvePic")
          ? payload.resolvePic
          : true,
        // A lookup by id (a detail page, often opened fresh from the URL)
        // must find the record whatever its state: without this a deleted
        // record's page loaded once from the list and then came back empty
        // on reload, because the search defaults to excluding deleted rows.
        includeDeleted: payload.includeDeleted ?? !!payload.ids?.length,
        // Created-on range from the filter panel's date filter.
        ...(payload.startDate && {
          startDate: `${payload.startDate}T00:00:00`,
        }),
        ...(payload.endDate && { endDate: `${payload.endDate}T23:59:59` }),
      };
      const response = await organisationApi.searchOrganisations(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      if (superseded()) return;
      dispatch(setTotalOrganisations(totalCount));

      const resolvedData = await Promise.all(
        fetchedData.map(
          async (
            org: Organisation & { medias?: any[]; profilePicUrl?: string },
          ) => {
            let updatedOrg = { ...org };

            // profilePic / logo only. displayNameDomainLogoUrl holds the
            // organisation's subdomain (the settings form reads and writes
            // it as such), so treating it as a logo media id sent
            // "e2ebucode.com" to getmedialink and logged a 400 for every
            // organisation that had a subdomain.
            const targetPic = org.profilePic || (org as any).logo;
            const looksLikeMediaId = (v: string) =>
              /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                v,
              );

            if (targetPic) {
              if (
                targetPic.startsWith("http") ||
                targetPic.startsWith("blob:")
              ) {
                updatedOrg.profilePicUrl = targetPic;
              } else if (looksLikeMediaId(targetPic)) {
                try {
                  const res = await organisationApi.getMediaLink(
                    [targetPic],
                    1,
                    org.id,
                  );
                  updatedOrg.profilePicUrl = res.links?.[0]?.link || "";
                } catch (err) {
                  console.error("Organisation logo resolution failed:", err);
                }
              }
            }

            if (org.medias && org.medias.length > 0) {
              try {
                const mediaIds = org.medias.map((m: any) => m.s3Url);
                const res = await organisationApi.getMediaLink(
                  mediaIds,
                  3,
                  org.id,
                );

                const resolvedMedias = org.medias.map((media: any) => {
                  const foundLink = res.links?.find(
                    (l: any) => l.id === media.s3Url,
                  );

                  return {
                    ...media,
                    url: foundLink ? foundLink.link : "",
                  };
                });

                updatedOrg.medias = resolvedMedias;
              } catch (err) {
                console.error("Organisation media resolve error:", err);
              }
            }

            return updatedOrg;
          },
        ),
      );

      const currentLoadedCount =
        incomingPageIndex === 0
          ? resolvedData.length
          : state.items.length + resolvedData.length;

      if (superseded()) return;
      if (currentLoadedCount >= totalCount || resolvedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setOrganisations(resolvedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendOrganisations(resolvedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Organisations error:", error);
      toast.error(
        i18n.t("organization:toast.fetchError", {
          defaultValue: "Failed to fetch organisations",
        }),
      );
    } finally {
      if (!superseded()) dispatch(setOrganisationLoading(false));
    }
  };
};

export const uploadOrganisationMedias = (
  organisationId: string,
  files: File[],
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!files?.length) return;

      const state = getState();
      const userId = (await getCurrentUser()).userId;

      const { payloads, links } = await uploadMediaFiles(
        organisationApi,
        organisationId,
        files,
        organisationId,
      );

      const newEntries = createResolvedMedia(
        payloads,
        links,
        organisationId,
        userId,
      );

      const updatedItems = state.organisations.items.map((item: any) =>
        item.id === organisationId
          ? {
              ...item,
              medias: [...(item.medias || []), ...newEntries],
              mediaLinks: null,
            }
          : item,
      );

      dispatch(setOrganisations(updatedItems));

      toast.success(
        i18n.t("organization:toast.uploadSuccess", {
          count: files.length,
          defaultValue: `${files.length} files uploaded successfully.`,
        }),
      );
    } catch (err) {
      console.error("Organisation Upload Error:", err);
      toast.error(
        i18n.t("organization:toast.uploadError", {
          defaultValue: "Upload failed",
        }),
      );
    }
  };
};

export const renameOrganisationMedia = (
  organisationId: string,
  fileId: string,
  newName: string,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const state = getState();

      const currentOrg = state.organisations.items.find(
        (org: any) => org.id === organisationId,
      );

      const targetFile = currentOrg?.medias?.find((m: any) => m.id === fileId);

      if (!targetFile) {
        toast.error(
          i18n.t("organization:toast.fileNotFoundError", {
            defaultValue: "File not found to rename",
          }),
        );
        return;
      }

      const payload = buildRenamePayload(targetFile, organisationId, newName);

      await organisationApi.uploadMedia(organisationId, payload);

      const updatedItems = state.organisations.items.map((item: any) =>
        item.id === organisationId
          ? {
              ...item,
              medias: applyRename(item.medias || [], fileId, newName),
            }
          : item,
      );

      dispatch(setOrganisations(updatedItems));

      toast.success(
        i18n.t("organization:toast.renameSuccess", {
          defaultValue: "File renamed successfully",
        }),
      );
    } catch (err) {
      console.error("Organisation Media Rename Error:", err);
      toast.error(
        i18n.t("organization:toast.renameError", {
          defaultValue: "Failed to rename file",
        }),
      );
    }
  };
};

/** Makes one of the organisation's photos its cover photo (null clears it). */
export const setOrganisationCover = (
  organisationId: string,
  fileId: string | null,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const { items } = getState().organisations;
    const medias =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches the slice's other media thunks
      items.find((org: any) => org.id === organisationId)?.medias || [];
    const payload = buildCoverPayload(medias, organisationId, fileId);
    if (payload.length === 0) return;
    try {
      await organisationApi.uploadMedia(organisationId, payload);
      dispatch(
        setOrganisations(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches the slice's other media thunks
          items.map((org: any) =>
            org.id === organisationId
              ? { ...org, medias: applyCover(org.medias || [], fileId) }
              : org,
          ),
        ),
      );
      toast.success(
        fileId
          ? i18n.t("slices.shared.coverUpdated", { ns: "common" })
          : i18n.t("slices.shared.coverRemoved", { ns: "common" }),
      );
    } catch (err) {
      toast.error(
        getApiErrorMessage(
          err,
          i18n.t("slices.shared.coverFailed", { ns: "common" }),
        ),
      );
      throw err;
    }
  };
};

export const removeOrganisationMedia = (
  organisationId: string,
  filesToRemove: any[],
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!filesToRemove?.length) return;

      const state = getState();

      const currentOrg = state.organisations.items.find(
        (org: any) => org.id === organisationId,
      );

      if (!currentOrg?.medias) return;

      const idsToRemove = filesToRemove.map((f: any) => f.id);

      const payload = buildDeletePayload(
        currentOrg.medias,
        organisationId,
        idsToRemove,
      );
      // Nothing to remove that this record still lists.
      if (payload.length === 0) return;

      await organisationApi.uploadMedia(organisationId, payload);

      const updatedItems = state.organisations.items.map((item: any) =>
        item.id === organisationId
          ? {
              ...item,
              medias: applyDelete(item.medias || [], idsToRemove),
            }
          : item,
      );

      dispatch(setOrganisations(updatedItems));

      toast.success(
        i18n.t("organization:toast.deleteImageSuccess", {
          defaultValue: "File removed",
        }),
      );
    } catch (err) {
      console.error("Organisation Media Remove Error:", err);
      toast.error(
        i18n.t("organization:toast.deleteImageError", {
          defaultValue: "Removal failed",
        }),
      );
    }
  };
};

export const addNewOrganisation = (formData: any) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const res = await organisationApi.createOrganisation(formData);
      // Inserted into the list optimistically (a refetch would read the
      // search index before it has the write), so stamp the audit fields
      // the card footer shows — without them the new card had no
      // Created/Updated row until the next reload.
      const now = new Date().toISOString();
      const me = await getCurrentUser();
      const newOrg = {
        ...formData,
        id: res.id || res,
        isActive: true,
        creationDate: now,
        modificationDate: now,
        createdBy: me.id,
        createdByName: me.fullName,
        modifiedBy: me.id,
        modifiedByName: me.fullName,
      };
      const { items } = getState().organisations;

      dispatch(setOrganisations([newOrg, ...items]));
      toast.success(
        i18n.t("organization:toast.addSuccess", {
          defaultValue: "Organisation created successfully!",
        }),
      );
    } catch (err) {
      console.error("Create Organisation Error:", err);
      // Re-thrown (with the backend's actual reason, when available) so
      // the caller can show it and knows the save actually failed, instead
      // of e.g. closing the modal as if it succeeded.
      throw new Error(
        getApiErrorMessage(
          err,
          i18n.t("organization:toast.addError", {
            defaultValue: "Failed to create organisation",
          }),
        ),
      );
    }
  };
};

export const editOrganisation = (formData: any) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const response = await organisationApi.updateOrganisation(formData);

      const updatedOrg =
        response?.data?.data || response?.data || response || formData;

      const targetId = formData.id || updatedOrg.id;

      const { items } = getState().organisations;

      const updatedList = items.map((item: Organisation) => {
        if (item.id === targetId) {
          return {
            ...item,
            ...formData,
            ...updatedOrg,
            isActive: formData.isActive ?? formData.status === "Active",
            status:
              formData.status || (formData.isActive ? "Active" : "Inactive"),
          };
        }
        return item;
      });

      dispatch(setOrganisations([...updatedList]));

      toast.success(
        i18n.t("organization:toast.updateSuccess", {
          defaultValue: "Organisation updated successfully!",
        }),
      );
    } catch (err) {
      console.error("Update Organisation Error:", err);
      throw new Error(
        getApiErrorMessage(
          err,
          i18n.t("organization:toast.editError", {
            defaultValue: "Update failed",
          }),
        ),
      );
    }
  };
};

/**
 * The restored row as the server now has it (its active state comes from history), once the read model
 * has caught up: polled by id for a few seconds, then swapped into the list.
 */
async function refreshRestoredOrganisation(id: string, dispatch: AppDispatch, getState: () => RootState) {
  for (let attempt = 0; attempt < 5; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    try {
      const res = await organisationApi.searchOrganisations({ pageIndex: 0, pageSize: 1, ids: [id], includeDeleted: true });
      const row = (res?.data ?? []).find((r: Organisation) => r?.id === id);
      if (row && !row.deletedBy && !row.deletionDate) {
        const { items } = getState().organisations;
        dispatch(setOrganisations(items.map((item: Organisation) => (item.id === id ? { ...item, ...row } : item))));
        return;
      }
    } catch {
      return;
    }
  }
}

/**
 * Brings a removed organisation back (Super Admin only). The row leaves its deleted state at once;
 * the caller reloads the list for the restored active state, which the server reads from history.
 */
export const restoreOrganisation = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
    try {
      await organisationApi.restoreOrganisation(id);
      const { items } = getState().organisations;
      dispatch(
        setOrganisations(
          items.map((item: Organisation) =>
            item.id === id ? { ...item, deletedBy: undefined, deletedByName: undefined, deletionDate: undefined } : item,
          ),
        ),
      );
      toast.success(
        i18n.t("organization:toast.restoreSuccess", { defaultValue: "Organisation restored" }),
      );
      void refreshRestoredOrganisation(id, dispatch, getState);
      return true;
    } catch (err) {
      console.error("Restore Organisation Error:", err);
      toast.error(
        getApiErrorMessage(err, i18n.t("organization:toast.restoreError", { defaultValue: "Could not restore the organisation" })),
      );
      return false;
    }
  };
};

/** Resolves true when the organisation was deleted. It announces the outcome
 *  itself (success or failure toast), so callers should not add their own. */
export const removeOrganisation = (id: string) => {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState,
  ): Promise<boolean> => {
    try {
      await organisationApi.removeOrganisation(id);
      const { items } = getState().organisations;

      dispatch(
        setOrganisations(items.filter((item: Organisation) => item.id !== id)),
      );

      toast.success(
        i18n.t("organization:toast.removeSuccess", {
          defaultValue: "Organisation removed successfully",
        }),
      );
      return true;
    } catch (err) {
      console.error("Delete Organisation Error:", err);
      toast.error(
        i18n.t("organization:toast.removeError", {
          defaultValue: "Delete failed",
        }),
      );
      return false;
    }
  };
};

export const uploadOrganisationProfilePic = async (
  file: File,
  organisationId?: string,
): Promise<string> => {
  const fileId = uuidv4();
  const initRes = await organisationApi.initUpload([fileId], organisationId);

  const uploadUrl = initRes.links[0].m;

  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": file.type },
  });

  return fileId;
};

const setOrganisationStatus = (id: string, isActive: boolean) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const key = isActive ? "activate" : "deactivate";
    try {
      if (isActive) await organisationApi.activateOrganisation(id);
      else await organisationApi.deactivateOrganisation(id);

      const { items } = getState().organisations;
      dispatch(
        setOrganisations(
          items.map((item: Organisation) =>
            item.id === id
              ? { ...item, status: isActive ? "Active" : "Inactive", isActive }
              : item,
          ),
        ),
      );
      toast.success(i18n.t(`organization:toast.${key}Success`));
      return true;
    } catch (err) {
      console.error(`Error on organisation ${key}:`, err);
      toast.error(i18n.t(`organization:toast.${key}Error`));
      return false;
    }
  };
};

export const handleActivateOrganisation = (id: string) =>
  setOrganisationStatus(id, true);

export const handleDeactivateOrganisation = (id: string) =>
  setOrganisationStatus(id, false);

interface OrganisationsState {
  items: Organisation[];
  isLoading: boolean;
  pageIndex: number;
  hasMore: boolean;
  totalOrganisations: number;
}

const initialState: OrganisationsState = {
  items: [],
  isLoading: false,
  pageIndex: 0,
  hasMore: true,
  totalOrganisations: 0,
};

const organisationsSlice = createSlice({
  name: "organisations",
  initialState,
  reducers: {
    setOrganisations: (state, action) => {
      state.items = action.payload;
      state.isLoading = false;
    },
    appendOrganisations: (state, action) => {
      state.items = [...state.items, ...action.payload];
      state.isLoading = false;
    },
    setPageIndex: (state, action) => {
      state.pageIndex = action.payload;
    },
    setHasMore: (state, action) => {
      state.hasMore = action.payload;
    },
    setTotalOrganisations: (state, action) => {
      state.totalOrganisations = action.payload;
    },
    setOrganisationLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    resetOrganisationList: (state) => {
      state.items = [];
      state.pageIndex = 0;
      state.hasMore = true;
    },
  },
});

export const {
  setOrganisations,
  setOrganisationLoading,
  resetOrganisationList,
  setHasMore,
  setPageIndex,
  appendOrganisations,
  setTotalOrganisations,
} = organisationsSlice.actions;

export default organisationsSlice.reducer;
